import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type { TestType } from '@risksmart-app/domain/src/types/consts/test-type';
import type { UpdateControlTestOversightResultApiMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

export type UpdateControlTestOversightResultInput = {
  Id: string;
  Title?: string | null;
  TestType?: TestType | null;
  Description?: string | null;
  DesignEffectiveness?: number | null;
  PerformanceEffectiveness?: number | null;
  OverallEffectiveness?: number | null;
  Submitter?: string | null;
  ParentControlId?: string | null;
  TestDate?: string | null;
  CustomAttributeData?: CustomAttributeData | null;
};

const mapTrpcResponseToGraphQL = (
  Id: string
): UpdateControlTestOversightResultApiMutation => ({
  updateControlTestOversightResultApi: {
    __typename: 'IdOutput',
    Id,
  },
});

export const useUpdateControlTestOversightResultTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.assessment.updateControlTestOversightResult.mutationOptions(
      {
        onSuccess: async () => {
          await Promise.all([
            // Apollo parity: evictField(cache, 'control_test_oversight_result')
            queryClient.invalidateQueries({
              queryKey:
                trpc.frontend.assessment.getOversightResultsByParentId.pathKey(),
            }),
            queryClient.invalidateQueries({
              queryKey:
                trpc.frontend.assessment.getAllOversightAssessmentResults.queryKey(),
            }),
            queryClient.invalidateQueries({
              queryKey: trpc.frontend.assessment.oversightResultById.pathKey(),
            }),
            // Apollo parity: evictField(cache, 'oversight_assessment')
            queryClient.invalidateQueries({
              queryKey:
                trpc.frontend.assessment.oversightAssessmentResultCount.queryKey(),
            }),
            queryClient.invalidateQueries({
              queryKey:
                trpc.frontend.assessment.oversightActivityRegister.queryKey(),
            }),
            queryClient.invalidateQueries({
              queryKey:
                trpc.frontend.assessment.oversightActivityCount.queryKey(),
            }),
          ]);
        },
      }
    ),
  });

  return {
    updateControlTestOversightResult: async (
      variables: UpdateControlTestOversightResultInput
    ): Promise<UpdateControlTestOversightResultApiMutation> => {
      const result = await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL(result.Id);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
